'use client';

import { useCallback } from 'react';
import type { Chat } from '../../types';
import { useChatHandlers, useMessageHandlers } from '../handlers';
import type { StartChatOptions } from '../ui/start-screen/useStartScreenComposer';
import type { FilePart } from '../../lib/db/types';
import type { AttachedSkill } from '../skills/types';

/** Entity context shape accepted by the underlying chat CRUD handlers. */
export interface ChatEntityContext {
  entityType: string;
  entityId: string;
  entityDisplayName?: string;
  entityData?: unknown;
}

/** Minimal slice of the chats infinite-query this controller needs. */
interface ChatsQueryLike {
  refetch: () => Promise<unknown>;
  hasNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
  data?: { id: string }[];
}

interface UseChatPageHandlersParams {
  apiBaseUrl: string;
  entityContext?: ChatEntityContext;
  chatsQuery: ChatsQueryLike;
  selectedChatId: string | null;
  selectedChat: Chat | null;
  setUrlChatId: (id: string | null) => void;
  setSelectedChat: (chat: Chat | null) => void;
  setInitialQuery: (query: string) => void;
  /** Files attached on the start screen, sent with the first message. */
  setInitialFiles: (files: FilePart[]) => void;
  /** Skill attached on the start screen, applied to the first message. */
  setInitialSkill: (skill: AttachedSkill | null) => void;
  setIsLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  isSidebarOpen: boolean;
  openDrawer: (id: string) => void;
  closeDrawer: (id: string) => void;
}

export interface ChatPageHandlers {
  handleCreateChat: () => Promise<void>;
  handleStartChatWithQuery: (query: string, options?: StartChatOptions) => Promise<void>;
  handleArchiveChat: (target?: { chatId: string; chat: Chat }) => Promise<void>;
  handleDeleteChat: (chatId?: string) => Promise<void>;
  handleRestoreChat: (chatId: string) => Promise<void>;
  handleDeleteAllChats: () => Promise<void>;
  handleCloseSidebar: () => void;
  handleToggleSidebar: () => void;
}

const MOBILE_SIDEBAR_DRAWER = 'mobile-sidebar-drawer';

/**
 * All ChatPage action handlers (chat CRUD + sidebar), extracted so ChatPage
 * stays a layout component. Behaviour is unchanged from the inline version.
 */
export function useChatPageHandlers({
  apiBaseUrl,
  entityContext,
  chatsQuery,
  selectedChatId,
  selectedChat,
  setUrlChatId,
  setSelectedChat,
  setInitialQuery,
  setInitialFiles,
  setInitialSkill,
  setIsLoading,
  setError,
  isSidebarOpen,
  openDrawer,
  closeDrawer,
}: UseChatPageHandlersParams): ChatPageHandlers {
  const { createChatHandler, archiveChatHandler, deleteChatHandler, restoreChatHandler, deleteAllChatsHandler } =
    useChatHandlers({ apiBaseUrl, onError: setError, entityContext });

  const { startChatWithQueryHandler } = useMessageHandlers({ apiBaseUrl, onError: setError });

  const handleCreateChat = useCallback(async () => {
    await createChatHandler(setUrlChatId, () => chatsQuery.refetch().then(() => undefined));
  }, [createChatHandler, setUrlChatId, chatsQuery]);

  const handleStartChatWithQuery = useCallback(
    async (query: string, options?: StartChatOptions) => {
      const files = options?.files ?? [];
      // Seed the first message's context BEFORE the chat id lands, so the
      // interface that mounts for the new chat sends text + files + skill in one
      // turn (same as pressing send inside an existing chat).
      setInitialFiles(files);
      setInitialSkill(options?.skill ?? null);
      await startChatWithQueryHandler(
        query,
        setUrlChatId,
        setInitialQuery,
        () => chatsQuery.refetch().then(() => undefined),
        setIsLoading,
        {
          ...(options?.chatId ? { clientChatId: options.chatId } : {}),
          allowEmptyQuery: files.length > 0,
        }
      );
    },
    [
      startChatWithQueryHandler,
      setUrlChatId,
      setInitialQuery,
      setInitialFiles,
      setInitialSkill,
      chatsQuery,
      setIsLoading,
    ]
  );

  // `target` lets a specific chat be archived (chat tabs: each open pane
  // archives ITS chat, which is not necessarily the selected one). Without it
  // the call falls back to the selected chat, as the conversation header does.
  const handleArchiveChat = useCallback(async (target?: { chatId: string; chat: Chat }) => {
    const targetChatId = target?.chatId ?? selectedChatId;
    const targetChat = target?.chat ?? selectedChat;
    const wasSelected = targetChatId === selectedChatId;
    await archiveChatHandler(
      targetChatId,
      targetChat,
      () => {
        if (wasSelected) setSelectedChat(null);
      },
      () => {
        if (wasSelected) setUrlChatId(null);
      },
      () => chatsQuery.refetch().then(() => undefined)
    );
  }, [archiveChatHandler, selectedChatId, selectedChat, chatsQuery, setSelectedChat, setUrlChatId]);

  const handleDeleteChat = useCallback(
    async (chatId?: string) => {
      // Honor the row's chatId (sidebar kebab/right-click); fall back to the
      // active chat when called with no arg (the conversation header delete).
      const targetChatId = chatId ?? selectedChatId;
      if (!targetChatId) return;
      // Only clear the active selection + URL when the chat being deleted IS
      // the one currently open — deleting a non-selected chat from the sidebar
      // must not blank the conversation the user is viewing.
      const wasSelected = targetChatId === selectedChatId;
      await deleteChatHandler(
        targetChatId,
        () => {
          if (wasSelected) setSelectedChat(null);
        },
        () => {
          if (wasSelected) setUrlChatId(null);
        },
        () => chatsQuery.refetch().then(() => undefined)
      );
    },
    [deleteChatHandler, selectedChatId, chatsQuery, setSelectedChat, setUrlChatId]
  );

  const handleRestoreChat = useCallback(
    async (chatId: string) => {
      await restoreChatHandler(chatId, () => chatsQuery.refetch().then(() => undefined));
    },
    [restoreChatHandler, chatsQuery]
  );

  const handleDeleteAllChats = useCallback(async () => {
    // Ensure the full list is loaded before collecting ids.
    while (chatsQuery.hasNextPage) {
      await chatsQuery.fetchNextPage();
    }
    const chatIds = chatsQuery.data?.map((c) => c.id) ?? [];
    await deleteAllChatsHandler(
      chatIds,
      () => setSelectedChat(null),
      () => setUrlChatId(null),
      () => chatsQuery.refetch().then(() => undefined)
    );
  }, [deleteAllChatsHandler, chatsQuery, setSelectedChat, setUrlChatId]);

  const handleCloseSidebar = useCallback(() => {
    closeDrawer(MOBILE_SIDEBAR_DRAWER);
  }, [closeDrawer]);

  const handleToggleSidebar = useCallback(() => {
    if (isSidebarOpen) {
      closeDrawer(MOBILE_SIDEBAR_DRAWER);
    } else {
      openDrawer(MOBILE_SIDEBAR_DRAWER);
    }
  }, [isSidebarOpen, openDrawer, closeDrawer]);

  return {
    handleCreateChat,
    handleStartChatWithQuery,
    handleArchiveChat,
    handleDeleteChat,
    handleRestoreChat,
    handleDeleteAllChats,
    handleCloseSidebar,
    handleToggleSidebar,
  };
}
